import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, ComposedChart
} from 'recharts';
import useXHR from '../../hooks/useXHR';

/**
 * Additional Analytics Charts for InfoPilot Explorer
 * Includes: User Engagement Over Time, Category Growth, Geographic Distribution
 */

// Color palette
const COLORS = [
  '#FDE047', '#10b981', '#3b82f6', '#f59e0b', '#ef4444',
  '#FDE68A', '#06b6d4', '#84cc16', '#F59E0B', '#FCD34D'
];

/**
 * UserEngagementChart - Shows user activity over time
 */
export const UserEngagementChart = ({ token, days = 30 }) => {
  const { get } = useXHR(token);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    get(`/analytics/user-engagement?days=${days}`, {
      onSuccess: (result) => setData(result.data || generateMockEngagementData(days)),
      onError: () => setData(generateMockEngagementData(days)),
      onFinally: () => setLoading(false)
    });
  }, [token, days]); // eslint-disable-line react-hooks/exhaustive-deps
  
  if (loading) {
    return <ChartLoader title="User Engagement" />;
  }
  
  return (
    <div style={chartContainerStyle}>
      <h4 style={chartTitleStyle}>📈 User Engagement Over Time</h4>
      <div style={{ height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data}>
            <defs>
              <linearGradient id="engagementGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FDE047" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#FDE047" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis dataKey="date" stroke="#6b7280" fontSize={10} />
            <YAxis stroke="#6b7280" fontSize={10} />
            <Tooltip 
              contentStyle={{ 
                background: 'rgba(30, 41, 59, 0.95)', 
                border: '1px solid rgba(252, 211, 77, 0.3)',
                borderRadius: 8
              }}
            />
            <Area 
              type="monotone" 
              dataKey="searches" 
              fill="url(#engagementGradient)" 
              stroke="#FDE047" 
              name="Searches"
            />
            <Line 
              type="monotone" 
              dataKey="categories" 
              stroke="#10b981" 
              strokeWidth={2}
              dot={{ fill: '#10b981', r: 3 }}
              name="Categories Created"
            />
            <Bar dataKey="reactions" fill="#f59e0b" opacity={0.7} name="Reactions" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div style={legendStyle}>
        <LegendItem color="#FDE047" label="Searches" />
        <LegendItem color="#10b981" label="Categories" />
        <LegendItem color="#f59e0b" label="Reactions" />
      </div>
    </div>
  );
};

/**
 * CategoryGrowthChart - Shows category creation trends
 */
export const CategoryGrowthChart = ({ token }) => {
  const { get } = useXHR(token);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    get('/analytics/category-growth', {
      onSuccess: (result) => setData(result.data || generateMockCategoryGrowth()),
      onError: () => setData(generateMockCategoryGrowth()),
      onFinally: () => setLoading(false)
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  
  if (loading) {
    return <ChartLoader title="Category Growth" />;
  }
  
  return (
    <div style={chartContainerStyle}>
      <h4 style={chartTitleStyle}>🌱 Category Growth</h4>
      <div style={{ height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis dataKey="month" stroke="#6b7280" fontSize={10} />
            <YAxis stroke="#6b7280" fontSize={10} />
            <Tooltip 
              contentStyle={{ 
                background: 'rgba(30, 41, 59, 0.95)', 
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: 8
              }}
            />
            <Area 
              type="monotone" 
              dataKey="total" 
              fill="url(#growthGradient)" 
              stroke="#10b981" 
              strokeWidth={2}
              name="Total Categories"
            />
            <Area 
              type="monotone" 
              dataKey="public" 
              fill="rgba(245, 158, 11, 0.2)" 
              stroke="#3b82f6" 
              strokeWidth={2}
              name="Public Categories"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/**
 * GeographicDistributionChart - Pie chart of content by location
 */
export const GeographicDistributionChart = ({ token }) => {
  const { get } = useXHR(token);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    get('/analytics/geographic-distribution', {
      onSuccess: (result) => setData(result.data || generateMockGeoData()),
      onError: () => setData(generateMockGeoData()),
      onFinally: () => setLoading(false)
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  
  if (loading) {
    return <ChartLoader title="Geographic Distribution" />;
  }
  
  return (
    <div style={chartContainerStyle}>
      <h4 style={chartTitleStyle}>🌍 Geographic Distribution</h4>
      <div style={{ height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
              labelLine={{ stroke: '#6b7280' }}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{ 
                background: 'rgba(30, 41, 59, 0.95)', 
                border: '1px solid rgba(253, 224, 71, 0.3)',
                borderRadius: 8
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/**
 * ProtocolComplexityRadar - Radar chart showing protocol characteristics
 */
export const ProtocolComplexityRadar = ({ token }) => {
  const { get } = useXHR(token);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    get('/analytics/protocol-complexity', {
      onSuccess: (result) => setData(result.data || generateMockRadarData()),
      onError: () => setData(generateMockRadarData()),
      onFinally: () => setLoading(false)
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  
  if (loading) {
    return <ChartLoader title="Protocol Complexity" />;
  }
  
  return (
    <div style={chartContainerStyle}>
      <h4 style={chartTitleStyle}>🎯 Protocol Complexity Analysis</h4>
      <div style={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}>
            <PolarGrid stroke="rgba(255,255,255,0.1)" />
            <PolarAngleAxis dataKey="metric" tick={{ fill: '#9ca3af', fontSize: 11 }} />
            <PolarRadiusAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
            <Radar 
              name="Your Protocols" 
              dataKey="user" 
              stroke="#FDE047" 
              fill="#FDE047" 
              fillOpacity={0.4} 
            />
            <Radar 
              name="Platform Average" 
              dataKey="average" 
              stroke="#10b981" 
              fill="#10b981" 
              fillOpacity={0.2} 
            />
            <Legend />
            <Tooltip 
              contentStyle={{ 
                background: 'rgba(30, 41, 59, 0.95)', 
                border: '1px solid rgba(253, 224, 71, 0.3)',
                borderRadius: 8
              }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/**
 * ContentTypeBreakdown - Stacked bar chart of content types over time
 */
export const ContentTypeBreakdown = ({ token }) => {
  const { get } = useXHR(token);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    get('/analytics/content-types', {
      onSuccess: (result) => setData(result.data || generateMockContentTypes()),
      onError: () => setData(generateMockContentTypes()),
      onFinally: () => setLoading(false)
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  
  if (loading) {
    return <ChartLoader title="Content Types" />;
  }
  
  return (
    <div style={chartContainerStyle}>
      <h4 style={chartTitleStyle}>📊 Content Type Breakdown</h4>
      <div style={{ height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis dataKey="period" stroke="#6b7280" fontSize={10} />
            <YAxis stroke="#6b7280" fontSize={10} />
            <Tooltip 
              contentStyle={{ 
                background: 'rgba(30, 41, 59, 0.95)', 
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: 8
              }}
            />
            <Bar dataKey="news" stackId="a" fill="#3b82f6" name="News" />
            <Bar dataKey="academic" stackId="a" fill="#FDE047" name="Academic" />
            <Bar dataKey="blog" stackId="a" fill="#10b981" name="Blog" />
            <Bar dataKey="social" stackId="a" fill="#FEF08A" name="Social" />
            <Bar dataKey="video" stackId="a" fill="#ef4444" name="Video" />
            <Legend />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Helper Components
const ChartLoader = ({ title }) => (
  <div style={{
    ...chartContainerStyle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200
  }}>
    <div style={{ textAlign: 'center', color: '#047857' }}>
      <div style={{ fontSize: '2rem', marginBottom: 10 }}>📊</div>
      Loading {title}...
    </div>
  </div>
);

const LegendItem = ({ color, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <div style={{ width: 10, height: 10, borderRadius: 2, background: color }} />
    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{label}</span>
  </div>
);

// Styles
const chartContainerStyle = {
  background: 'rgba(30, 41, 59, 0.95)',
  borderRadius: 16,
  padding: 20,
  border: '1px solid rgba(252, 211, 77, 0.2)'
};

const chartTitleStyle = {
  color: '#0F172A',
  margin: '0 0 15px 0',
  fontSize: '1rem',
  fontWeight: 600
};

const legendStyle = {
  display: 'flex',
  justifyContent: 'center',
  gap: 20,
  marginTop: 10
};

// Mock data generators (used when API fails)
const generateMockEngagementData = (days) => {
  const data = [];
  for (let i = days; i > 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    data.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      searches: Math.floor(Math.random() * 50) + 10,
      categories: Math.floor(Math.random() * 10) + 1,
      reactions: Math.floor(Math.random() * 30) + 5
    });
  }
  return data;
};

const generateMockCategoryGrowth = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  return months.map((month, i) => ({
    month,
    total: 20 + i * 15 + Math.floor(Math.random() * 10),
    public: 10 + i * 8 + Math.floor(Math.random() * 5)
  }));
};

const generateMockGeoData = () => [
  { name: 'USA', value: 45 },
  { name: 'Europe', value: 25 },
  { name: 'Asia', value: 15 },
  { name: 'Other', value: 15 }
];

const generateMockRadarData = () => [
  { metric: 'OR Groups', user: 75, average: 60 },
  { metric: 'AND Groups', user: 65, average: 50 },
  { metric: 'Exclusions', user: 45, average: 30 },
  { metric: 'Boosts', user: 55, average: 40 },
  { metric: 'Phrases', user: 35, average: 25 },
  { metric: 'Complexity', user: 70, average: 55 }
];

const generateMockContentTypes = () => {
  const periods = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
  return periods.map(period => ({
    period,
    news: Math.floor(Math.random() * 30) + 20,
    academic: Math.floor(Math.random() * 20) + 10,
    blog: Math.floor(Math.random() * 25) + 15,
    social: Math.floor(Math.random() * 15) + 5,
    video: Math.floor(Math.random() * 10) + 5
  }));
};

/**
 * MostActiveUsersChart - Shows most active users in the system
 */
export const MostActiveUsersChart = ({ token }) => {
  const { get } = useXHR(token);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    get('/analytics/most-active-users', {
      onSuccess: (result) => setData(result.users || generateMockActiveUsers()),
      onError: () => setData(generateMockActiveUsers()),
      onFinally: () => setLoading(false)
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  
  if (loading) {
    return <ChartLoader title="Most Active Users" />;
  }
  
  return (
    <div style={chartContainerStyle}>
      <h4 style={chartTitleStyle}>🏆 Most Active Users</h4>
      <div style={{ height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis type="number" stroke="#6b7280" fontSize={10} />
            <YAxis dataKey="name" type="category" stroke="#6b7280" fontSize={10} width={80} />
            <Tooltip 
              contentStyle={{ 
                background: 'rgba(30, 41, 59, 0.95)', 
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: 8
              }}
            />
            <Bar dataKey="activity" fill="#10b981" radius={[0, 4, 4, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Generate mock active users data
const generateMockActiveUsers = () => {
  const names = ['User1', 'User2', 'User3', 'User4', 'User5'];
  return names.map(name => ({
    name,
    activity: Math.floor(Math.random() * 500) + 100,
    searches: Math.floor(Math.random() * 200),
    categories: Math.floor(Math.random() * 50)
  }));
};

/**
 * TopLikedUrlsChart - Shows URLs with most reactions/likes
 */
export const TopLikedUrlsChart = ({ token }) => {
  const { get } = useXHR(token);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    get('/analytics/top-liked-urls', {
      onSuccess: (result) => setData(result.urls || generateMockTopUrls()),
      onError: () => setData(generateMockTopUrls()),
      onFinally: () => setLoading(false)
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  
  if (loading) {
    return <ChartLoader title="Top Liked URLs" />;
  }
  
  return (
    <div style={chartContainerStyle}>
      <h4 style={chartTitleStyle}>❤️ Top Liked URLs</h4>
      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
        {data.map((item, idx) => (
          <div 
            key={idx}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              background: 'rgba(255, 255, 255, 0.85)',
              borderRadius: 8,
              marginBottom: 6,
              border: '1px solid rgba(255, 255, 255, 0.85)'
            }}
          >
            <span style={{
              background: 'linear-gradient(135deg, #ef4444, #FDE68A)',
              color: '#0F172A',
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              fontWeight: 700
            }}>
              {idx + 1}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '0.8rem',
                color: '#334155',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {item.title}
              </div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                {item.domain}
              </div>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              background: 'rgba(239, 68, 68, 0.2)',
              padding: '4px 10px',
              borderRadius: 12,
              color: '#f87171',
              fontSize: '0.75rem',
              fontWeight: 600
            }}>
              ❤️ {item.likes}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Generate mock top URLs data
const generateMockTopUrls = () => {
  const titles = [
    'Understanding Quantum Computing Basics',
    'The Complete Guide to Machine Learning',
    'Web Development Best Practices 2024',
    'Introduction to Blockchain Technology',
    'Data Science for Beginners'
  ];
  return titles.map((title, idx) => ({
    title,
    domain: ['wikipedia.org', 'medium.com', 'github.com', 'arxiv.org', 'dev.to'][idx],
    likes: Math.floor(Math.random() * 200) + 50,
    reactions: Math.floor(Math.random() * 100)
  }));
};

/**
 * RealTimeActivityMonitor - Shows live activity feed with animated bars
 */
export const RealTimeActivityMonitor = ({ token, refreshInterval = 5000 }) => {
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({ searches: 0, categorizations: 0, shares: 0 });
  
  useEffect(() => {
    const generateActivity = () => {
      const types = ['search', 'categorize', 'share', 'view', 'export'];
      const users = ['User_A', 'User_B', 'User_C', 'User_D', 'User_E'];
      const categories = ['Tech News', 'Research', 'Finance', 'Health', 'Education'];
      
      return {
        id: Date.now(),
        type: types[Math.floor(Math.random() * types.length)],
        user: users[Math.floor(Math.random() * users.length)],
        category: categories[Math.floor(Math.random() * categories.length)],
        timestamp: new Date().toISOString()
      };
    };
    
    const addActivity = () => {
      const newActivity = generateActivity();
      setActivities(prev => [newActivity, ...prev.slice(0, 9)]);
      
      setStats(prev => ({
        searches: newActivity.type === 'search' ? prev.searches + 1 : prev.searches,
        categorizations: newActivity.type === 'categorize' ? prev.categorizations + 1 : prev.categorizations,
        shares: newActivity.type === 'share' ? prev.shares + 1 : prev.shares
      }));
    };
    
    // Initial activities
    for (let i = 0; i < 5; i++) addActivity();
    
    const interval = setInterval(addActivity, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);
  
  const getActivityIcon = (type) => {
    const icons = {
      search: '🔍',
      categorize: '📁',
      share: '🔗',
      view: '👁️',
      export: '📤'
    };
    return icons[type] || '📊';
  };
  
  const getActivityColor = (type) => {
    const colors = {
      search: '#3b82f6',
      categorize: '#FDE047',
      share: '#10b981',
      view: '#f59e0b',
      export: '#FDE68A'
    };
    return colors[type] || '#6b7280';
  };
  
  return (
    <div style={chartContainerStyle}>
      <h4 style={chartTitleStyle}>⚡ Real-Time Activity Monitor</h4>
      
      {/* Stats row */}
      <div style={{ display: 'flex', gap: 15, marginBottom: 15, flexWrap: 'wrap' }}>
        <div style={{ 
          background: 'rgba(245, 158, 11, 0.15)', 
          padding: '10px 15px', 
          borderRadius: 8,
          flex: 1,
          minWidth: 80,
          textAlign: 'center'
        }}>
          <div style={{ color: '#3b82f6', fontSize: '1.4rem', fontWeight: 700 }}>{stats.searches}</div>
          <div style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>Searches</div>
        </div>
        <div style={{ 
          background: 'rgba(253, 224, 71, 0.15)', 
          padding: '10px 15px', 
          borderRadius: 8,
          flex: 1,
          minWidth: 80,
          textAlign: 'center'
        }}>
          <div style={{ color: '#FDE047', fontSize: '1.4rem', fontWeight: 700 }}>{stats.categorizations}</div>
          <div style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>Categorizations</div>
        </div>
        <div style={{ 
          background: 'rgba(16, 185, 129, 0.15)', 
          padding: '10px 15px', 
          borderRadius: 8,
          flex: 1,
          minWidth: 80,
          textAlign: 'center'
        }}>
          <div style={{ color: '#10b981', fontSize: '1.4rem', fontWeight: 700 }}>{stats.shares}</div>
          <div style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>Shares</div>
        </div>
      </div>
      
      {/* Activity feed */}
      <div style={{ maxHeight: 250, overflowY: 'auto' }}>
        {activities.map((activity, idx) => (
          <div 
            key={activity.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '8px 12px',
              background: idx === 0 ? 'rgba(253, 224, 71, 0.1)' : 'transparent',
              borderRadius: 8,
              marginBottom: 4,
              animation: idx === 0 ? 'fadeIn 0.3s ease-out' : 'none',
              borderLeft: `3px solid ${getActivityColor(activity.type)}`
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>{getActivityIcon(activity.type)}</span>
            <div style={{ flex: 1 }}>
              <span style={{ color: '#e5e5e5', fontSize: '0.85rem' }}>
                {activity.user}
              </span>
              <span style={{ color: '#6b7280', fontSize: '0.85rem' }}> {activity.type}d </span>
              <span style={{ color: getActivityColor(activity.type), fontSize: '0.85rem' }}>
                {activity.category}
              </span>
            </div>
            <span style={{ color: '#4b5563', fontSize: '0.7rem' }}>
              {new Date(activity.timestamp).toLocaleTimeString()}
            </span>
          </div>
        ))}
      </div>
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

/**
 * ActivityHeatmap - Shows activity intensity by day/hour
 */
export const ActivityHeatmap = ({ token }) => {
  const [heatmapData, setHeatmapData] = useState([]);
  
  useEffect(() => {
    // Generate mock heatmap data (7 days x 24 hours)
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const data = [];
    
    for (let day = 0; day < 7; day++) {
      for (let hour = 0; hour < 24; hour++) {
        // More activity during business hours on weekdays
        const isWeekday = day > 0 && day < 6;
        const isBusinessHour = hour >= 9 && hour <= 17;
        const baseIntensity = (isWeekday && isBusinessHour) ? 0.6 : 0.2;
        
        data.push({
          day: days[day],
          dayIndex: day,
          hour,
          intensity: Math.min(1, baseIntensity + Math.random() * 0.4)
        });
      }
    }
    
    setHeatmapData(data);
  }, []);
  
  const getHeatColor = (intensity) => {
    if (intensity < 0.2) return 'rgba(253, 224, 71, 0.1)';
    if (intensity < 0.4) return 'rgba(253, 224, 71, 0.25)';
    if (intensity < 0.6) return 'rgba(253, 224, 71, 0.45)';
    if (intensity < 0.8) return 'rgba(253, 224, 71, 0.65)';
    return 'rgba(253, 224, 71, 0.9)';
  };
  
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  return (
    <div style={chartContainerStyle}>
      <h4 style={chartTitleStyle}>🗓️ Activity Heatmap</h4>
      <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0 0 15px 0' }}>
        Activity intensity by day and hour
      </p>
      
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto repeat(24, 1fr)', gap: 2, minWidth: 600 }}>
          {/* Hour labels */}
          <div style={{ padding: '4px 8px' }}></div>
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} style={{ 
              textAlign: 'center', 
              fontSize: '0.6rem', 
              color: '#6b7280',
              padding: 2
            }}>
              {h}
            </div>
          ))}
          
          {/* Day rows */}
          {days.map((day, dayIdx) => (
            <React.Fragment key={day}>
              <div style={{ 
                padding: '4px 8px', 
                fontSize: '0.75rem', 
                color: '#a1a1aa',
                display: 'flex',
                alignItems: 'center'
              }}>
                {day}
              </div>
              {Array.from({ length: 24 }, (_, hour) => {
                const cell = heatmapData.find(d => d.dayIndex === dayIdx && d.hour === hour);
                return (
                  <div
                    key={hour}
                    style={{
                      background: getHeatColor(cell?.intensity || 0),
                      borderRadius: 2,
                      height: 20,
                      minWidth: 16,
                      cursor: 'pointer',
                      transition: 'transform 0.1s'
                    }}
                    title={`${day} ${hour}:00 - ${Math.round((cell?.intensity || 0) * 100)}% activity`}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
      
      {/* Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 15, justifyContent: 'center' }}>
        <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>Low</span>
        {[0.1, 0.25, 0.45, 0.65, 0.9].map((intensity, idx) => (
          <div
            key={idx}
            style={{
              width: 16,
              height: 16,
              background: getHeatColor(intensity),
              borderRadius: 3
            }}
          />
        ))}
        <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>High</span>
      </div>
    </div>
  );
};

export default {
  UserEngagementChart,
  CategoryGrowthChart,
  GeographicDistributionChart,
  ProtocolComplexityRadar,
  ContentTypeBreakdown,
  MostActiveUsersChart,
  TopLikedUrlsChart,
  RealTimeActivityMonitor,
  ActivityHeatmap
};
